import { Box, Button } from '@mui/material';
import sortBy from 'lodash/sortBy';
import { DateTime } from 'luxon';
import {
  type Dispatch,
  type FC,
  type SetStateAction,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';

import { SlabContext } from '@core/context/SlabContext';
import {
  FormikQuote,
  FormikQuoteStatus,
  NewQuoteFromFormik,
  type QuoteFormikType,
  type QuoteSubmitType,
} from '@core/formik/quote';
import { getFilteredProductsRefsByFormikProducts } from '@core/hooks/useProductSection';
import type Enums from '@core/types/api/Enums';
import type { Product } from '@core/types/api/Product/Product';
import type { Quote } from '@core/types/api/Quote/Quote';
import type { QuotePolicy } from '@core/types/api/QuotePolicy/QuotePolicy';
import { QuoteStatus } from '@core/types/api/QuoteStatus/QuoteStatus';
import type { UserInfo } from '@core/types/api/UserInfo/UserInfo';
import { FileEntityType } from '@core/types/file';
import { QuoteEditLockStatus } from '@core/types/quoteEditLock';
import type { TenantDefaultMargins } from '@core/types/tenantDefaultMargins';
import { toRecordById } from '@core/utils/domainHelpers';
import { hasValue } from '@core/utils/equality';
import { generateAutoProjectName } from '@core/utils/quoteDrawerUtilities';
import { findApprovalRequestStatus } from '@core/utils/quoteHelpers';
import { NIL_UUID, isValidUUID } from '@core/utils/uuid';
import {
  type FileUploadsManager,
  attachBufferedEntityFiles,
  useFileUploads,
} from '@web/components/Files';
import { LoadingSpinner } from '@web/components/LoadingSpinner/LoadingSpinner';
import { NylasDrawerPage } from '@web/components/Nylas/NylasDrawer';
import { SlabDrawer } from '@web/components/SlabDrawer/SlabDrawer';
import { useDrawerCloseGuard } from '@web/components/SlabDrawer/useDrawerCloseGuard';
import { SlabServerError } from '@web/components/SlabServerError/SlabServerError';
import { useSlabMutation } from '@web/hooks/useSlabMutation';
import { ServerErrorMessage, useSlabQuery, useSlabQueryFetcher } from '@web/hooks/useSlabQuery';
import { useTenantMargins } from '@web/hooks/useTenantMargins';
import { useToast } from '@web/providers/ToastProvider/useToast';
import type { MakeToastBuilderReturn } from '@web/providers/ToastProvider/utils/makeToastBuilder';
import type { QueryRouteBarrelTypes, QuoteCreateBody } from '@web/utils/ApiClient';
import type { QueryError } from '@web/utils/Query';
import { getTenantRequiresApprovals } from '@web/utils/TenantConfig';

import { DispatchCustomerModals } from '../DispatchCustomerCheck/DispatchCustomerModals';
import { useDispatchCustomerCheck } from '../DispatchCustomerCheck/useDispatchCustomerCheck';
import { QuoteDrawerApprovedPage } from './common/QuoteDrawerApprovedPage';
import { QuoteDrawerMainPage } from './common/QuoteDrawerMainPage';
import type { QuoteSection } from './common/QuoteDrawerSections';
import { QuoteDrawerViolationsPage } from './common/QuoteDrawerViolationsPage';
import { useQuoteEditLockOwnership } from './useQuoteEditLock';
import {
  type QuickQuoteAddress,
  type QuoteDrawerFormikType,
  type QuoteDrawerState,
  emptyQuickQuoteAddress,
} from './utils';

const toQuickModeFormik = (quote: QuoteFormikType): QuoteDrawerFormikType => ({
  ...quote,
  quickProjectMode: 'new',
  quickAddress: emptyQuickQuoteAddress(),
});

type StepAfterLinkDispatch = 'saveAsApproved' | 'sendQuote' | 'save';

export enum QuoteDrawerMode {
  Quick = 'quick',
  Full = 'full',
}

type QouteDrawerContextType = {
  readOnly?: boolean;
  /** Set while another user's edit lock keeps this drawer view-only. */
  lockedBy?: { message: string; editorName: string | null };
  isReconcilingBuyerPricing?: boolean;
  setIsReconcilingBuyerPricing?: (reconciling: boolean) => void;
};

const QuoteDrawerContext = createContext<QouteDrawerContextType>({ readOnly: false });

export const useQuoteDrawerContext = (): QouteDrawerContextType => {
  return useContext(QuoteDrawerContext);
};

type CreateQuoteBody = QuoteCreateBody & {
  autoCreateProject?: boolean;
  projectName?: string;
  address?: QuickQuoteAddress;
};

export type QuoteDrawerProps = {
  resourceID: string | null;
  submitType: QuoteSubmitType;
  makeDefaultFormikQuote: (data: {
    userInfo: UserInfo;
    submitType: QuoteSubmitType;
    isMoneySwitchOn: boolean;
    marginConfiguration: Enums.MarginConfiguration;
    defaultMargins: TenantDefaultMargins;
  }) => QuoteFormikType;

  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  onRequestSuccess: (quote: Quote) => void;
  onNylasSuccess: (quoteID: string) => void;
  onRequestError: (error: unknown, defaultErrorMessage?: string) => void;
  onNylasError: (message: string) => void;
  onClose?: () => void;

  /** @default 'Quote Information' */
  initialSection?: QuoteSection;

  readOnly?: boolean;

  mode?: QuoteDrawerMode;

  openedFromProject?: boolean;
};

export const attachBufferedQuoteFiles = (
  manager: FileUploadsManager,
  quoteId: string,
  toast: MakeToastBuilderReturn,
): void => attachBufferedEntityFiles(manager, quoteId, toast, FileEntityType.Quote);

export const QuoteDrawer: FC<QuoteDrawerProps> = ({
  resourceID,
  submitType,
  makeDefaultFormikQuote,
  initialSection,
  onRequestError,
  onNylasError,
  onRequestSuccess,
  onNylasSuccess,
  isOpen,
  setIsOpen,
  onClose,
  readOnly,
  mode = QuoteDrawerMode.Full,
  openedFromProject = false,
}) => {
  const ctx = useContext(SlabContext);
  const dispatch = useDispatchCustomerCheck();

  const navigate = useNavigate();
  const { guardClose } = useDrawerCloseGuard();
  const { defaultIsMoneySwitchOn, marginConfiguration, defaultMargins } = useTenantMargins();

  const [stepAfterLinkDispatch, setStepAfterLinkDispatch] = useState<StepAfterLinkDispatch>();

  const [drawerError, setDrawerError] = useState<string | null | undefined>(null);
  const [isPlantArchived, setIsPlantArchived] = useState(false);
  const [isQuoteArchived, setIsQuoteArchived] = useState(false);
  const [isApprovalRequestPending, setIsApprovalRequestPending] = useState(false);
  const [loadedQuoteID, setLoadedQuoteID] = useState<string | null>(null);

  const [drawerState, setDrawerState] = useState<QuoteDrawerState>('quoteLoading');
  const [formKey, setFormKey] = useState(0);
  const [editedQuote, setEditedQuote] = useState<QuoteDrawerFormikType>(
    toQuickModeFormik(
      makeDefaultFormikQuote({
        userInfo: ctx.userInfo,
        submitType,
        isMoneySwitchOn: defaultIsMoneySwitchOn,
        marginConfiguration,
        defaultMargins,
      }),
    ),
  );
  // We prefer `useRef` here because it allows for immediate mutable changes to the
  // state. If we use `useState` we see "delays" between setting values vs. rendering,
  // which causes form-values to fall out-of-sync with state-values.
  const curProductsRef = useRef<Record<string, Product>>({});
  const newQuoteInitializedRef = useRef(false);
  // Tracks the (resourceID, submitType) combo we've already loaded so a later
  // quoteStatuses refetch does not re-fire the existing-quote fetch and stomp
  // unsaved edits.
  const existingQuoteInitializedKeyRef = useRef<string | null>(null);
  const loadTokenRef = useRef(0);
  // Bumped when the lock frees up for a view-only drawer, which then reloads what the editor saved.
  const [quoteReloadCount, setQuoteReloadCount] = useState(0);
  const [policyViolations, setPolicyViolations] = useState<QuotePolicy[]>([]);

  const quoteFetcher = useSlabQueryFetcher<
    'GET quote by ID',
    QueryRouteBarrelTypes['GET quote by ID'],
    QueryError
  >();
  const projectProductsFetcher = useSlabQueryFetcher<
    'GET project products by project ID',
    QueryRouteBarrelTypes['GET project products by project ID'],
    QueryError
  >();

  const {
    isLoading: isLoadingQuoteStatuses,
    data: quoteStatuses,
    refetch: refetchQuoteStatuses,
  } = useSlabQuery('GET quote statuses', {});
  const { isLoading: isLoadingQuotePolicies, data: quotePolicies } = useSlabQuery(
    'GET quote policies',
    {},
  );

  const currentQuoteStatus = useMemo(() => {
    return quoteStatuses?.items.find((s) => s.id === editedQuote.status.id) ?? QuoteStatus.zero();
  }, [editedQuote.status.id, quoteStatuses]);

  const tenantRequiresApprovals = getTenantRequiresApprovals({
    tenant: ctx.userInfo.tenant,
    quotePolicies,
  });

  // Buffered upload queue for the Files step in create mode (no quote ID exists yet
  // to attach files to). Lives here -- above QuoteDrawerMainPage/QuoteDrawerSections --
  // so it survives step navigation (MUI StepContent unmounts inactive steps) and so the
  // create-save success handler below can reach it to attach queued files.
  const cloneSourceQuoteID =
    submitType === 'saveAsNewQuote' && isValidUUID(resourceID) ? resourceID : null;
  const filesManager = useFileUploads({
    entityType: FileEntityType.Quote,
    entityId: null,
    cloneSourceEntityId: cloneSourceQuoteID,
  });
  const filesToast = useToast({ subjectName: 'file' });

  const createNew = useSlabMutation('POST quote', {
    onSuccess: (createdQuote): void => {
      attachBufferedQuoteFiles(filesManager, createdQuote.id, filesToast);
      onRequestSuccess(createdQuote);
    },
  });

  const updateExisting = useSlabMutation('PUT quote by ID', {
    onSuccess: onRequestSuccess,
  });

  const findViolations = useSlabMutation('POST check quote for policy violations');

  const isExistingRecord = isValidUUID(resourceID);

  // A new revision or a duplicate writes a new quote, so only editing this one takes the lock.
  const lockOwnership = useQuoteEditLockOwnership(
    resourceID ?? '',
    isOpen && submitType === 'save' && isExistingRecord && readOnly !== true,
    () => setQuoteReloadCount((count) => count + 1),
  );
  const lockedBy = useMemo(
    () =>
      lockOwnership.status === QuoteEditLockStatus.Blocked && hasValue(lockOwnership.blockedMessage)
        ? { message: lockOwnership.blockedMessage, editorName: lockOwnership.editorName }
        : undefined,
    [lockOwnership.status, lockOwnership.blockedMessage, lockOwnership.editorName],
  );

  const [isReconcilingBuyerPricing, setIsReconcilingBuyerPricing] = useState(false);

  const drawerContextValue = useMemo(
    () => ({ readOnly, lockedBy, isReconcilingBuyerPricing, setIsReconcilingBuyerPricing }),
    [readOnly, lockedBy, isReconcilingBuyerPricing],
  );

  // Callback functions to handle each state machine transition that might be
  // triggered while the drawer is open.

  const onErrorQuote = (queryError: unknown, defaultErrorMessage: string): void => {
    setDrawerState('error');

    setDrawerError(ServerErrorMessage(queryError, defaultErrorMessage));
  };

  const closeDrawer = (): void => {
    dispatch.resetDispatchState();
    setStepAfterLinkDispatch(undefined);
    setIsOpen(false);
    setDrawerState('quoteLoading');
    setEditedQuote(
      toQuickModeFormik(
        makeDefaultFormikQuote({
          userInfo: ctx.userInfo,
          submitType,
          isMoneySwitchOn: defaultIsMoneySwitchOn,
          marginConfiguration,
          defaultMargins,
        }),
      ),
    );
    curProductsRef.current = {};
    onClose?.();
  };

  const redirectToQuote = (quoteID: string): void => {
    navigate(`/quotes/${quoteID}`);
  };

  const closeDrawerAndRedirectIfNewQuote = (quoteID: string): void => {
    closeDrawer();

    if (submitType !== 'save') {
      // Delay navigation so React can render the drawer closing first.
      // Without this, Suspense may keep the old UI visible during lazy loading
      // and the drawer appears stuck open. With the delay, the drawer closes
      // immediately and the user is redirected to the next page, where a spinner
      // is shown while the lazy chunk loads on slow connections.
      setTimeout(() => redirectToQuote(quoteID), 0);
    }
  };

  const setEditedQuoteAfterUpsert = (formData: QuoteDrawerFormikType, apiData: Quote): void => {
    setEditedQuote({
      ...formData,
      // Persist the server's version of values that might be changed by the
      // server, and retain the user's original data for all other properties.
      id: apiData.id,
      quoteNumber: apiData.quoteNumber,
      revisionNumber: apiData.revisionNumber,
      status: FormikQuoteStatus(apiData.status),
    });
  };

  /**
   * Performs the actual server-side upsert operation for a given Quote.
   *
   * Note: Despite the name, this function directly calls the server mutation.
   * It is not just building a request payload. This differs from `upsertQuote`,
   * which may wrap this function with additional business logic or transformations.
   */
  const upsertQuoteRequest = async (
    quote: Quote,
    extraBodyFields?: Pick<CreateQuoteBody, 'autoCreateProject' | 'projectName' | 'address'>,
  ): Promise<Quote> => {
    if (isValidUUID(quote.id)) {
      const updatedQuote = await updateExisting.mutateAsync({
        args: { pathParams: { id: quote.id }, body: quote },
      });

      return updatedQuote;
    }

    const createdQuote = await createNew.mutateAsync({
      args: {
        body: {
          ...quote,
          ...extraBodyFields,
          copyFilesFromId: cloneSourceQuoteID ?? undefined,
          excludeFileIds: filesManager.excludedCloneFileIds,
        } as CreateQuoteBody,
      },
    });

    return createdQuote;
  };

  // Callers of upsertQuote must setEditedQuote as appropriate.
  const upsertQuote = async (values: QuoteDrawerFormikType): Promise<Quote> => {
    const request = NewQuoteFromFormik(values);

    let extraBodyFields:
      | Pick<CreateQuoteBody, 'autoCreateProject' | 'projectName' | 'address'>
      | undefined;

    if (mode === QuoteDrawerMode.Quick) {
      const hasExistingProject = hasValue(values.project?.id) && values.project.id !== '';
      if (!hasExistingProject) {
        const projectName = generateAutoProjectName(values.name ?? '', ctx.userInfo.tenant.locale);

        extraBodyFields = {
          autoCreateProject: true,
          projectName,
          address: values.quickAddress,
        };
      }
    }

    const upsertedQuote = await upsertQuoteRequest(request, extraBodyFields);

    curProductsRef.current = toRecordById(upsertedQuote.products, (qp) => qp.product);

    return upsertedQuote;
  };

  const upsertQuoteWithoutApprovalsEnabled = async (
    values: QuoteDrawerFormikType,
  ): Promise<void> => {
    try {
      const savedQuote = await upsertQuote(values);
      // Don't need to setEditedQuote or curProductsRef because closeDrawer will clear that state.
      closeDrawer();

      if (submitType !== 'save') redirectToQuote(savedQuote.id);
    } catch (error) {
      onRequestError(error, 'Failed to save the quote');
    }
  };

  const upsertQuoteAsApprovalRequest = async (): Promise<Quote> => {
    setIsApprovalRequestPending(true);
    try {
      const firstApprovalRequestStatus = await findApprovalRequestStatus(
        quoteStatuses,
        refetchQuoteStatuses,
      );

      const values = { ...editedQuote, status: FormikQuoteStatus(firstApprovalRequestStatus) };

      const savedQuote = await upsertQuote(values);

      setEditedQuoteAfterUpsert(values, savedQuote);

      return savedQuote;
    } finally {
      setIsApprovalRequestPending(false);
    }
  };

  const upsertQuoteAsDraft = async (): Promise<void> => {
    const statusList = quoteStatuses ?? { items: [] };

    const firstDraftStatus = statusList.items
      .sort((a, b) => a.priority - b.priority)
      .find((s) => s.isDraft());

    const values = { ...editedQuote, status: FormikQuoteStatus(firstDraftStatus) };

    const savedQuote = await upsertQuote(values);

    closeDrawerAndRedirectIfNewQuote(savedQuote.id);
  };

  const checkQuoteForViolations = async (values: QuoteDrawerFormikType): Promise<void> => {
    setEditedQuote(values);

    // If transitioning to an archived status, always save and immediately
    // close the drawer (without checking for violations or offering to send
    // the quote).  See sc-4164.

    const isArchivedStatus = (quoteStatuses?.items ?? []).some(
      (status) => status.id === values.status.id && status.isArchived(),
    );

    if (isArchivedStatus) {
      try {
        const savedQuote = await upsertQuote(values);

        setEditedQuoteAfterUpsert(values, savedQuote);

        closeDrawer();
      } catch (error) {
        onRequestError(
          error,
          'Failed to save the archived quote. Please try again or contact support if the issue persists',
        );
      }

      return;
    }

    // Quick-mode quotes without an existing project will auto-create one during
    // upsert. The checkpolicies endpoint doesn't support autoCreateProject, so
    // skip the policy check and save directly.
    const isQuickModeNoProject =
      mode === QuoteDrawerMode.Quick && (!hasValue(values.project?.id) || values.project.id === '');

    if (isQuickModeNoProject) {
      try {
        const savedQuote = await upsertQuote(values);

        setEditedQuoteAfterUpsert(values, savedQuote);

        setDrawerState('confirmApproved');
      } catch (error) {
        onRequestError(error, 'Failed to save the quote');
      }

      return;
    }

    try {
      const violations = await findViolations.mutateAsync({
        args: { body: NewQuoteFromFormik(values) },
      });

      setPolicyViolations(violations.violatedPolicies);

      if (violations.violatedPolicies.length > 0) {
        setDrawerState('reviewViolations');

        return;
      }

      try {
        const savedQuote = await upsertQuote(values);

        setEditedQuoteAfterUpsert(values, savedQuote);

        setDrawerState('confirmApproved');
      } catch (error) {
        onRequestError(error, 'Failed to save the quote after violation check');
      }
    } catch (error) {
      onRequestError(error, 'Failed to check the quote for policy violations');
    }
  };

  const checkDispatchCustomers = async (formData: QuoteDrawerFormikType): Promise<boolean> => {
    setEditedQuote(formData);

    if (!hasValue(formData.company?.id)) {
      return true;
    }

    const productsRecord = getFilteredProductsRefsByFormikProducts({
      curProductsRef: curProductsRef.current,
      formikProductsByPlants: [
        ...formData.mainProductsByPlants,
        ...formData.additionalProductsByPlants,
      ],
    });

    return dispatch.checkDispatchCustomers({
      companyId: formData.company.id,
      productsRecord,
    });
  };

  const handleSubmitForm = async (formData: QuoteDrawerFormikType): Promise<void> => {
    if (tenantRequiresApprovals) {
      await checkQuoteForViolations(formData);
      return;
    }

    await upsertQuoteWithoutApprovalsEnabled(formData);
  };

  const handleSaveForm = async (formData: QuoteDrawerFormikType): Promise<void> => {
    const isStatusExportable = quoteStatuses?.items.some(
      (status) => status.isBuyerAccepted() && status.id === formData.status.id,
    );

    if (!dispatch.usesDispatchCustomer || tenantRequiresApprovals || !isStatusExportable) {
      await handleSubmitForm(formData);
      return;
    }

    const dispatchCustomerCheckSuccess = await checkDispatchCustomers(formData);

    if (!dispatchCustomerCheckSuccess) {
      setStepAfterLinkDispatch('save');
      return;
    }

    await handleSubmitForm(formData);
  };

  const handleViolationSubmitForm = async (): Promise<void> => {
    try {
      const savedQuote = await upsertQuote(editedQuote);

      closeDrawerAndRedirectIfNewQuote(savedQuote.id);
    } catch (error) {
      onRequestError(error, 'Failed to submit the quote');
    }
  };

  const handleViolationSubmitForReview = async (): Promise<void> => {
    try {
      const savedQuote = await upsertQuoteAsApprovalRequest();

      closeDrawerAndRedirectIfNewQuote(savedQuote.id);
    } catch (error) {
      onRequestError(error, 'Failed to submit the quote for review');
    }
  };

  const handleSaveAsApproved = async (): Promise<void> => {
    try {
      const savedQuote = await upsertQuoteAsApprovalRequest();
      closeDrawerAndRedirectIfNewQuote(savedQuote.id);
    } catch (error) {
      onRequestError(error, 'Failed to prepare the quote for sending');
    }
  };

  const handleSendQuote = async (): Promise<void> => {
    try {
      await upsertQuoteAsApprovalRequest();
      setDrawerState('send');
    } catch (error) {
      onRequestError(error, 'Failed to prepare the quote for sending');
    }
  };

  // This useEffect handles initial state management for curProductsRef: when
  // the drawer opens, we initialize that reference to the Products in the
  // initial quote data (if any); when the drawer closes, we empty out that
  // reference to avoid memory leaks.
  useEffect(() => {
    // If the drawer should be closed, make sure it is.
    if (!isOpen) {
      newQuoteInitializedRef.current = false;
      existingQuoteInitializedKeyRef.current = null;
      loadTokenRef.current += 1;
      if (drawerState !== 'quoteLoading') {
        closeDrawer();
        // If we ever implement sc-144, we will maybe need to redirect to the
        // editedQuote's detail page here if the drawerState and submitType
        // indicate that a new Quote object has been created in this drawer.
      }

      return;
    }

    // If this is a new quote, the initial data is Quote.zero().
    if (!isValidUUID(resourceID)) {
      // Wait until quote statuses load so we can seed a valid Draft status. If
      // we initialize with QuoteStatus.zero(), Yup rejects status.id (NIL_UUID)
      // and the user cannot submit. Only initialize once per open.
      if (quoteStatuses === undefined || newQuoteInitializedRef.current) {
        return;
      }
      newQuoteInitializedRef.current = true;
      // A new quote is never archived. Clear any archive state left over from a
      // previously loaded existing quote so DrawerFormFooter shows Save/Reset.
      setIsQuoteArchived(false);
      setIsPlantArchived(false);
      setLoadedQuoteID(null);
      const initialQuote = makeDefaultFormikQuote({
        userInfo: ctx.userInfo,
        submitType,
        isMoneySwitchOn: defaultIsMoneySwitchOn,
        marginConfiguration,
        defaultMargins,
      });

      const draftStatus =
        sortBy(quoteStatuses?.items ?? [], (s) => s.priority).find((s): boolean => s.isDraft()) ??
        QuoteStatus.zero();

      setEditedQuote(
        toQuickModeFormik({
          ...initialQuote,
          status: {
            id: draftStatus.id,
            option: { value: draftStatus.id, label: draftStatus.name },
            systemBehavior: draftStatus.systemBehavior,
          },
        }),
      );
      setFormKey((k) => k + 1);

      // If this new quote has a default project, load its product information.
      // This is important to have cost information available to the form's calculations.
      if (isValidUUID(initialQuote.project.id)) {
        loadTokenRef.current += 1;
        const loadToken = loadTokenRef.current;
        projectProductsFetcher('GET project products by project ID', {
          pathParams: { id: initialQuote.project.id ?? '' },
        })
          .then((projectProducts) => {
            if (loadTokenRef.current !== loadToken) return;
            curProductsRef.current = toRecordById(projectProducts, (pp) => pp.product);
            setDrawerState('edit');
          })
          .catch((error) => {
            if (loadTokenRef.current !== loadToken) return;
            onErrorQuote(error, 'Failed to get products for a project');
          });

        return;
      }

      // If the default new quote data does not reference a project, it has no products.
      curProductsRef.current = {};
      setDrawerState('edit');
      return;
    }

    // If this is based on an existing quote ('save' for existing quote, or
    // 'saveAsNewRevision' / 'saveAsNewQuote'), load initial data from our API.
    // Only re-fetch when the (resourceID, submitType) pair actually changes —
    // otherwise a background quoteStatuses revalidation would re-enter this
    // branch and overwrite the user's in-progress edits.
    const existingKey = `${resourceID}|${submitType}|${quoteReloadCount}`;
    if (existingQuoteInitializedKeyRef.current === existingKey) {
      return;
    }
    existingQuoteInitializedKeyRef.current = existingKey;
    loadTokenRef.current += 1;
    const loadToken = loadTokenRef.current;
    setDrawerState('quoteLoading');
    quoteFetcher(
      'GET quote by ID',
      { pathParams: { id: resourceID ?? '' } },
      quoteReloadCount > 0 ? { staleTime: 0 } : undefined,
    )
      .then(async (quote) => {
        const projectProducts = isValidUUID(quote.project.id)
          ? await projectProductsFetcher('GET project products by project ID', {
              pathParams: { id: quote.project.id },
            }).catch((error) => {
              if (submitType === 'save') return undefined;
              throw error;
            })
          : undefined;

        if (loadTokenRef.current !== loadToken) return;

        const formikValues = FormikQuote(
          quote,
          dispatch.usesDispatchCustomer,
          submitType,
          defaultIsMoneySwitchOn,
          marginConfiguration,
          defaultMargins,
          projectProducts,
        );
        if (submitType === 'save') {
          setEditedQuote(toQuickModeFormik(formikValues));
          setFormKey((k) => k + 1);
        } else {
          const allStatuses = quoteStatuses ?? { items: [] };
          const firstDraftStatus =
            sortBy(allStatuses.items, (s) => s.priority).find((s) => s.isDraft()) ??
            QuoteStatus.zero();
          const currentDate = DateTime.now().toISODate();

          // If there were previously a revision date set, default to today's date.
          const revisionDate =
            formikValues.revisionDate !== null && formikValues.revisionDate !== ''
              ? currentDate
              : formikValues.revisionDate;

          setEditedQuote(
            toQuickModeFormik({
              ...formikValues,
              id: NIL_UUID,
              status: FormikQuoteStatus(firstDraftStatus),
              creationDate: currentDate,
              revisionDate,
            }),
          );
          setFormKey((k) => k + 1);
        }
        curProductsRef.current = toRecordById(quote.products, (qp) => qp.product);
        setIsPlantArchived(quote.plants.some((qp) => qp.plant.isArchived));
        setIsQuoteArchived(quote.status.isArchived());
        setLoadedQuoteID(quote.id);

        setDrawerState('edit');
      })
      .catch((error) => {
        if (loadTokenRef.current !== loadToken) return;
        onErrorQuote(error, 'Failed to load the quote');
      });
  }, [isOpen, resourceID, submitType, quoteStatuses, quoteReloadCount]);

  useEffect(() => {
    if (!isOpen) {
      filesManager.reset();
    }
    // filesManager identity intentionally excluded: it changes with every queue
    // update, and only isOpen transitions should trigger this reset.
  }, [isOpen]);

  const triggerNextStepAfterLinkDispatch = (): void => {
    switch (stepAfterLinkDispatch) {
      case 'saveAsApproved':
        handleSaveAsApproved();
        break;
      case 'sendQuote':
        handleSendQuote();
        break;
      case 'save':
        handleSaveForm(editedQuote);
        break;
      default:
        break;
    }
  };

  const saveAsDraftCallback = !tenantRequiresApprovals
    ? (): void => {
        upsertQuoteAsDraft();
        dispatch.resetDispatchState();
      }
    : undefined;

  // The form page guards its own close; past it, the violations step still holds an unsaved quote.
  const closeOutsideForm = (): void => {
    // Closing mid-request would drop what it opens next, such as the send composer.
    if (createNew.isPending || updateExisting.isPending || isApprovalRequestPending) return;
    if (drawerState === 'confirmApproved') {
      closeDrawerAndRedirectIfNewQuote(editedQuote.id);
      return;
    }
    guardClose({
      isDirty: drawerState === 'reviewViolations',
      canSave: true,
      isSaving: false,
      onSave: handleViolationSubmitForm,
      onDiscard: closeDrawer,
    });
  };

  return (
    <QuoteDrawerContext.Provider value={drawerContextValue}>
      <>
        <SlabDrawer
          isOpen={isOpen}
          close={closeOutsideForm}
          // Required in the scenario a user closes then reopens the same drawer. Product state
          // will not be updated due to single-hook rendering values.
          slideProps={{ unmountOnExit: true }}
          paperProps={
            drawerState !== 'confirmApproved'
              ? { sx: { width: '80rem', maxWidth: '100%' } }
              : undefined
          }
        >
          {(isLoadingQuoteStatuses ||
            isLoadingQuotePolicies ||
            drawerState === 'quoteLoading' ||
            drawerState === 'violationsLoading') && <LoadingSpinner />}

          {drawerState === 'edit' && (
            <QuoteDrawerMainPage
              key={formKey}
              formikQuote={editedQuote}
              curProductsRef={curProductsRef}
              submitForm={handleSaveForm}
              cancelForm={closeDrawer}
              submitType={submitType}
              isSubmitPending={
                createNew.isPending || updateExisting.isPending || findViolations.isPending
              }
              initialSection={initialSection}
              mode={mode}
              isPlantArchived={isPlantArchived}
              isQuoteArchived={isQuoteArchived}
              loadedQuoteID={loadedQuoteID}
              filesManager={filesManager}
              openedFromProject={openedFromProject}
              onArchiveSuccess={(q): void => {
                setIsQuoteArchived(q.isArchived);
                closeDrawer();
              }}
            />
          )}

          {drawerState === 'reviewViolations' && (
            <QuoteDrawerViolationsPage
              violatedPolicies={policyViolations}
              submitForm={handleViolationSubmitForm}
              submitForReview={handleViolationSubmitForReview}
              isSubmitPending={
                updateExisting.isPending || createNew.isPending || isApprovalRequestPending
              }
            />
          )}

          {drawerState === 'confirmApproved' && (
            <QuoteDrawerApprovedPage
              onClose={() => closeDrawerAndRedirectIfNewQuote(editedQuote.id)}
              onAutoApprove={handleSaveAsApproved}
              onApproveAndSend={handleSendQuote}
              onSend={() => setDrawerState('send')}
              isSubmitPending={updateExisting.isPending || isApprovalRequestPending}
              currentQuoteStatus={currentQuoteStatus}
            />
          )}

          {drawerState === 'send' && (
            <NylasDrawerPage
              quoteID={editedQuote.id}
              setIsOpen={setIsOpen}
              onSuccess={() => {
                closeDrawerAndRedirectIfNewQuote(editedQuote.id);
                onNylasSuccess(editedQuote.id);
              }}
              onError={onNylasError}
            />
          )}

          {drawerState === 'error' && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                flex: 1,
                padding: '5rem 3.5rem 5rem 3.5rem',
              }}
            >
              {hasValue(drawerError) && <SlabServerError errorMessage={drawerError} />}

              <Button variant='slab-secondary' onClick={closeDrawer}>
                Close
              </Button>
            </Box>
          )}
        </SlabDrawer>
        <DispatchCustomerModals
          selectedCompany={dispatch.selectedCompany}
          selectedCompanyWithMatches={dispatch.selectedCompanyWithMatches}
          mismatchProducts={dispatch.mismatchProducts}
          isLinkCustomerModalOpen={dispatch.isLinkCustomerModalOpen}
          isMergeCustomerModalOpen={dispatch.isMergeCustomerModalOpen}
          isCompanyWithoutDispatchModalOpen={dispatch.isCompanyWithoutDispatchModalOpen}
          isDispatchCustomerMismatchModalOpen={dispatch.isDispatchCustomerMismatchModalOpen}
          onSubmit={triggerNextStepAfterLinkDispatch}
          onSaveAsDraft={saveAsDraftCallback}
          onCloseMerge={() => {
            dispatch.setIsMergeCustomerModalOpen(false);
            dispatch.setSelectedCompanyWithMatches(null);
          }}
          onCloseLink={() => dispatch.setIsLinkCustomerModalOpen(false)}
          onCloseWithoutDispatch={() => dispatch.setIsCompanyWithoutDispatchModalOpen(false)}
          onCloseMismatch={() => {
            dispatch.setIsDispatchCustomerMismatchModalOpen(false);
            dispatch.setMismatchProducts([]);
          }}
        />
      </>
    </QuoteDrawerContext.Provider>
  );
};
